import clsx from "clsx";
import React, { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import {
    DESKTOP_MENU_PANEL_ID,
    FOCUSABLE_MENU_ITEM_SELECTOR,
} from "web/react/components/navigation/navigation.constants";
import {
    MenuCategoryType,
    useNavigationContext,
} from "web/react/components/navigation/navigation.context";
import { isShopfrontsMenu } from "web/react/components/navigation/shopfronts.constants";
import { Text } from "web/react/emo/text";
import { MenuLinkSerializer, SubMenuSerializer } from "web/types/serializers";
import * as styles from "./sticky-categories.css";

function getMenuEventLabel(menu: SubMenuSerializer): MenuCategoryType {
    return isShopfrontsMenu(menu) ? "shopfronts" : "product_type";
}

// a tap or a click focuses the category too, and those already have their own
// handlers, so only a keyboard focus should open the panel. Browsers (and jsdom)
// that cannot match :focus-visible throw, and fall back to opening on any focus
function isKeyboardFocus(element: HTMLElement): boolean {
    try {
        return element.matches(":focus-visible");
    } catch {
        return true;
    }
}

function focusFirstPanelItem(): void {
    const panel = document.getElementById(DESKTOP_MENU_PANEL_ID);
    panel?.querySelector<HTMLElement>(FOCUSABLE_MENU_ITEM_SELECTOR)?.focus();
}

export interface StickyCategoriesProps {
    menus: SubMenuSerializer[];
    links: MenuLinkSerializer[];
    selectedCategory: SubMenuSerializer | null;
    setSelectedCategory: Dispatch<SetStateAction<SubMenuSerializer | null>>;
}

export function StickyCategories({
    menus,
    links,
    selectedCategory,
    setSelectedCategory,
}: StickyCategoriesProps): React.ReactElement {
    const { showMenu, setShowMenu, handleCategoryAnalytics } = useNavigationContext();
    // This is required for tablets in horizontal mode
    // First touch click would be to open the menu
    // Second touch click would be to close the menu
    const [shouldRedirect, setShouldRedirect] = useState(true);

    const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
    const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openCategoryPanel = useCallback(
        function (menu: SubMenuSerializer): void {
            setSelectedCategory(menu);
            setShowMenu("desktop");
            handleCategoryAnalytics(getMenuEventLabel(menu), "impression");
        },
        [handleCategoryAnalytics, setSelectedCategory, setShowMenu]
    );

    const onClick = useCallback(
        function (event, redirect: boolean, eventLabel: MenuCategoryType | null = null): void {
            if (!redirect && !shouldRedirect) {
                event.preventDefault();
                return;
            }
            const analyticsEventLabel = eventLabel ? eventLabel : "product_type";
            handleCategoryAnalytics(analyticsEventLabel);
        },
        [shouldRedirect, handleCategoryAnalytics]
    );

    // plain links have no panel of their own, so highlighting one closes whichever
    // category panel was open
    const highlightPlainLink = useCallback(
        function (link?: MenuLinkSerializer): void {
            if (link) {
                const menu: SubMenuSerializer = {
                    display_name: link.event_label || "",
                    links: [],
                    name: link.event_label || "",
                    url: link.url,
                };
                setSelectedCategory(menu);
            } else {
                setSelectedCategory(null);
            }
            setShowMenu(null);
        },
        [setSelectedCategory, setShowMenu]
    );

    const onTouchClick = useCallback(
        (menu: SubMenuSerializer) => {
            setShouldRedirect(false);
            if (showMenu === null) {
                openCategoryPanel(menu);
            } else {
                highlightPlainLink();
            }
        },
        [openCategoryPanel, highlightPlainLink, showMenu]
    );

    const onMouseEnter = (menu: SubMenuSerializer): void => {
        setHoveredMenu(menu.display_name);
        hoverTimeout.current = setTimeout(() => {
            openCategoryPanel(menu);
        }, styles.HOVER_DELAY_IN_MS);
    };

    const onMouseLeave = (): void => {
        setHoveredMenu(null);
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
            hoverTimeout.current = null;
        }
    };

    function isMenuExpanded(menu: SubMenuSerializer): boolean {
        return showMenu === "desktop" && selectedCategory?.display_name === menu.display_name;
    }

    function onCategoryFocus(
        event: React.FocusEvent<HTMLAnchorElement>,
        menu: SubMenuSerializer
    ): void {
        if (isMenuExpanded(menu) || !isKeyboardFocus(event.currentTarget)) {
            return;
        }

        openCategoryPanel(menu);
    }

    function onCategoryKeyDown(
        event: React.KeyboardEvent<HTMLAnchorElement>,
        menu: SubMenuSerializer
    ): void {
        if (event.key !== "ArrowDown") {
            return;
        }

        event.preventDefault();

        if (isMenuExpanded(menu)) {
            focusFirstPanelItem();
        } else {
            openCategoryPanel(menu);
        }
    }

    return (
        <nav>
            <div className={styles.container}>
                {menus.map((menu, i) => (
                    <a
                        data-testid={`category-${menu.display_name}`}
                        className={clsx(
                            styles.stickyItem,
                            hoveredMenu === menu.display_name && styles.loading,
                            selectedCategory?.display_name === menu.display_name && styles.active
                        )}
                        key={`${i}-${menu.display_name}`}
                        aria-controls={DESKTOP_MENU_PANEL_ID}
                        aria-expanded={isMenuExpanded(menu)}
                        aria-haspopup="true"
                        onMouseEnter={() => onMouseEnter(menu)}
                        onMouseLeave={onMouseLeave}
                        onTouchStart={() => onTouchClick(menu)}
                        onFocus={(event) => onCategoryFocus(event, menu)}
                        onKeyDown={(event) => onCategoryKeyDown(event, menu)}
                        href={menu.url}
                        onClick={(event) => onClick(event, false, getMenuEventLabel(menu))}
                    >
                        <Text textStyle="body-s">{menu.display_name}</Text>
                    </a>
                ))}

                {links.map((link, i) => (
                    <a
                        data-testid={`link-${link.text}`}
                        className={clsx(styles.stickyItem, {
                            [styles.active]: selectedCategory?.display_name === link.event_label,
                        })}
                        key={`${i}-${link.text}`}
                        href={link.url}
                        onMouseEnter={() => highlightPlainLink(link)}
                        onFocus={() => highlightPlainLink(link)}
                        onClick={(event) =>
                            onClick(event, true, link.event_label as MenuCategoryType)
                        }
                    >
                        <Text textStyle="body-s" color={link.is_sale ? "error" : "primary"}>
                            {link.text}
                        </Text>
                    </a>
                ))}
            </div>
        </nav>
    );
}
